
import {defineComponent, PropType} from "vue";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {EyebrowRestDtoModel} from "@/models/api/pharma-cms-content-page/EyebrowRestDtoModel";
import {EyebrowStyleEnum} from "@/api/enums/eyebrow-style-enum";
import {TextColorValueRestDtoModel} from "@/models/api/TextColorValueRestDtoModel";
import InputSelect from "@/components/UI/InputSelect.vue";
import InputText from "@/components/UI/InputText.vue";
import CssDimensionEditor from "@/components/layouts/content-pages/wysiwyg/misc/CssDimensionEditor.vue";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import MetadataGroupCard from "@/components/layouts/content-pages/wysiwyg/misc/MetadataGroupCard.vue";
import AdvancedColorEditor from "@/components/layouts/content-pages/wysiwyg/misc/AdvancedColorEditor.vue";
import {ColorValueRestDtoModel} from "@/models/api/ColorValueRestDtoModel";
import {EyebrowRestDto} from "@/api/pharma-cms-content-page/models";

export default defineComponent({
  name: "EyebrowDetail",
  mixins: [ComponentMixin],
  components: {
    AdvancedColorEditor, MetadataGroupCard,
    GenericComponentWrapper, InputSelect, InputText, CssDimensionEditor},
  emits: ['update:modelValue'],
  props: {
    // Null means the group is switched off.
    modelValue: {
      type: Object as PropType<EyebrowRestDto | null>,
      required: false,
      default: null,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.initialize();
  },
  data() {
    return {
      enabled: false as boolean,
      localDto: EyebrowRestDtoModel.createWithDefaults() as EyebrowRestDto,
      // Values entered before the group was switched off, so they can be restored.
      lastActiveDto: null as EyebrowRestDto | null,
      // Reset baseline: the state as it is stored in the database.
      persistedDto: null as EyebrowRestDto | null,
      enumStyleCode: [] as CodeDescriptionRestDto[],
      enumBarWidthCode: [] as CodeDescriptionRestDto[],
      enumTextTransformationCode: [] as CodeDescriptionRestDto[],
      enumFontWeightCode: [] as CodeDescriptionRestDto[],
      enumMarginCode: [] as CodeDescriptionRestDto[],
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: EyebrowRestDto | null) {
        this.enabled = !!newValue;
        if (newValue) {
          this.localDto = EyebrowRestDtoModel.cloneFrom(newValue);
        }
      },
    },
  },
  computed: {
    isBar(): boolean {
      return this.localDto.style_code === EyebrowStyleEnum.BAR;
    },
    isPill(): boolean {
      return this.localDto.style_code === EyebrowStyleEnum.PILL;
    },
    isKicker(): boolean {
      return this.localDto.style_code === EyebrowStyleEnum.KICKER;
    },
    showText(): boolean {
      return !this.isBar;
    },
    showColor(): boolean {
      return !this.isKicker;
    },
    showTextColor(): boolean {
      return this.isKicker || this.isPill;
    },
    showLetterSpacing(): boolean {
      return this.localDto.style_code === EyebrowStyleEnum.TAG
        || this.localDto.style_code === EyebrowStyleEnum.KICKER;
    },
    showBarWidth(): boolean {
      return this.isBar;
    },
  },
  methods: {
    onEnabledChanged(newValue: boolean): void {
      if (newValue === this.enabled) {
        return;
      }
      this.enabled = newValue;

      if (newValue) {
        this.localDto = this.lastActiveDto
          ? EyebrowRestDtoModel.cloneFrom(this.lastActiveDto)
          : EyebrowRestDtoModel.createWithDefaults();

        this.$emit('update:modelValue', this.localDto);
      } else {
        this.lastActiveDto = EyebrowRestDtoModel.cloneFrom(this.localDto);

        this.$emit('update:modelValue', null);
      }
    },
    resetToPersisted(): void {
      this.localDto = this.persistedDto
        ? EyebrowRestDtoModel.cloneFrom(this.persistedDto)
        : EyebrowRestDtoModel.createWithDefaults();

      this.$emit('update:modelValue', this.localDto);
    },
    async doInitialize(): Promise<void> {
      [
        this.enumStyleCode,
        this.enumBarWidthCode,
        this.enumMarginCode,
        this.enumTextTransformationCode,
        this.enumFontWeightCode,
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.EYEBROW_STYLE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.EYEBROW_BAR_WIDTH, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.MARGIN, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.TEXT_TRANSFORMATION, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.FONT_WEIGHT_ENUM, DpModuleEnum.CMS),
      ]);
    },
    onStyleChanged(): void {
      if (this.showColor) {
        if (!this.localDto.color) {
          this.localDto.color = ColorValueRestDtoModel.createWithDefaults();
        }
      } else {
        this.localDto.color = null;
      }
      if (this.showTextColor) {
        if (!this.localDto.text_color) {
          this.localDto.text_color = TextColorValueRestDtoModel.createWithDefaults();
        }
      } else {
        this.localDto.text_color = null;
      }
      this.emitModelChanged();
    },
    emitModelChanged(): void {
      this.$emit('update:modelValue', this.localDto);
    }
  },
});
